import React, { useEffect, useMemo, useRef, useState } from "react";
import { ownersApi } from "../lib/api";
import { csvCell } from "../lib/utils";
import { Card, AddToCampaignModal, useOutreach, ComposeEmailModal, useCompose } from "../components/shared";
import { SendToFolkModal } from "../components/SendToFolkModal";
import { toast } from "sonner";
import { Upload, Plus, Trash2, Download, Save, Share2, Users, Mail } from "lucide-react";

const TYPES = ["Owner-operator", "Family owned", "Retiring owner", "Multi-location", "Franchisee", "Investor owned", "Other"];
const STAGES = ["Prospect", "Contacted", "Talking", "Diligence", "Passed"];
const S = (v) => String(v ?? "").trim();
const num = (v) => { const n = Number(String(v ?? "").replace(/[^0-9.-]/g, "")); return isNaN(n) || !n ? null : n; };

const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const delim = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
  const split = (l) => l.split(delim).map((c) => c.trim().replace(/^"|"$/g, ""));
  const hdr = split(lines[0]).map((h) => h.toLowerCase());
  const col = (...keys) => hdr.findIndex((h) => keys.some((k) => h.includes(k)));
  const i = { name: col("owner", "name", "contact", "principal"), company: col("company", "business", "dba"), type: col("type", "category", "segment"),
    title: col("title", "role"), email: col("email"), phone: col("phone", "tel"), website: col("website", "url", "domain"),
    city: col("city", "town"), state: col("state", "province"), revenue: col("revenue", "sales"), employees: col("employee", "staff", "headcount"),
    notes: col("note", "comment") };
  const first = hdr.findIndex((h) => h === "first name" || h === "first");
  const last = hdr.findIndex((h) => h === "last name" || h === "last");
  return lines.slice(1).map((l) => {
    const c = split(l);
    const g = (k) => (i[k] >= 0 ? S(c[i[k]]) : "");
    const name = first >= 0 ? `${S(c[first])} ${last >= 0 ? S(c[last]) : ""}`.trim() : g("name");
    return { name, company: g("company"), type: g("type"), title: g("title"), email: g("email").split(/[;,\s]/)[0], phone: g("phone"),
      website: g("website"), city: g("city"), state: g("state"), revenue: num(g("revenue")),
      employees: num(g("employees")) != null ? Math.round(num(g("employees"))) : null, notes: g("notes"), source: "upload" };
  }).filter((x) => x.name || x.company);
};

const BLANK = { name: "", company: "", type: "", title: "", email: "", phone: "", city: "", state: "", revenue: "", employees: "", notes: "" };

export default function OwnerList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [typeOpts, setTypeOpts] = useState([]);
  const [sel, setSel] = useState({});
  const [folkOpen, setFolkOpen] = useState(false);
  const outreach = useOutreach();
  const compose = useCompose();
  const picked = rows.filter((r) => sel[r.id]);
  const withEmail = picked.filter((r) => r.email);
  const toContact = (o) => ({ id: o.id, name: o.name || o.company, email: o.email, company: o.company,
    title: o.title || "Owner", phone: o.phone, website: o.website, notes: [o.type, o.city, o.state].filter(Boolean).join(" · ") });
  const toggleAll = () => {
    const all = rows.length > 0 && rows.every((r) => sel[r.id]);
    setSel(all ? {} : Object.fromEntries(rows.map((r) => [r.id, true])));
  };
  const addToCampaign = () => {
    if (!withEmail.length) return toast.error("Selected owners need an email address");
    outreach.start(withEmail.map(toContact), `${type || "Owner list"} — owner outreach`, "owners");
  };
  const openCompose = () => {
    if (!withEmail.length) return toast.error("Selected owners need an email address");
    const o = withEmail[0];
    compose.start({
      title: "Email business owner(s)",
      recipients: withEmail.map((x) => ({ name: x.name || x.company, email: x.email })),
      subject: `${o.company || "your business"} — a quick, confidential question`,
      body: `Hi ${(o.name || "").split(" ")[0] || "there"},\n\nI'm with XYZ Ventures. We buy and hold well-run ${(o.type || "local").toLowerCase()} businesses and keep the teams in place.\n\nI have no idea whether you've ever thought about selling or bringing on a partner, but if the timing is ever right I'd like to be your first call.\n\nWorth a confidential 15 minutes? Owner to owner, no brokers.\n\nBest,\nXYZ Ventures`,
      kind: "owners",
      vars: [["Owner", o.name || "—"], ["Company", o.company || "—"], ["List", o.type || "—"]],
    });
  };
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState(BLANK);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [list, t] = await Promise.all([ownersApi.list({ q, type }), ownersApi.types()]);
      setRows(list);
      setTypeOpts(t.types || []);
    } catch { toast.error("Could not load the owner list"); }
  };
  useEffect(() => { load(); }, [q, type]); // eslint-disable-line

  const add = async () => {
    if (!f.name && !f.company) return toast.error("Enter an owner name or a company");
    try {
      await ownersApi.create({ ...f, revenue: num(f.revenue), employees: num(f.employees) != null ? Math.round(num(f.employees)) : null });
      toast.success("Owner added");
      setF(BLANK); setAdding(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const parsed = parseCsv(String(reader.result));
      if (!parsed.length) return toast.error("No rows found — needs a header row with Owner / Company");
      const label = (window.prompt("Name this list (niche / company type):", type || "") || "").trim();
      try {
        const r = await ownersApi.bulk(label ? parsed.map((x) => ({ ...x, type: x.type || label })) : parsed);
        toast.success(`${r.added} owners added${r.updated ? `, ${r.updated} updated` : ""}`);
        if (label) setType(label);
        load();
      } catch { toast.error("Upload failed"); }
    };
    reader.readAsText(file);
  };

  const patch = async (o, p) => {
    setRows((rs) => rs.map((r) => (r.id === o.id ? { ...r, ...p } : r)));
    try { await ownersApi.update(o.id, p); } catch { toast.error("Could not save"); load(); }
  };
  const del = async (o) => {
    if (!window.confirm(`Remove ${o.name || o.company}?`)) return;
    try { await ownersApi.remove(o.id); setRows((rs) => rs.filter((r) => r.id !== o.id)); } catch { toast.error("Delete failed"); }
  };
  const exportCsv = () => {
    const cols = ["name", "company", "type", "title", "email", "phone", "city", "state", "revenue", "employees", "stage", "notes"];
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => csvCell(r[c])).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "owner-list.csv";
    a.click();
  };

  const counts = useMemo(() => rows.reduce((a, r) => ({ ...a, [r.type || "Other"]: (a[r.type || "Other"] || 0) + 1 }), {}), [rows]);

  return (
    <div className="page" data-testid="page-owners">
      <div className="page-head">
        <div>
          <div className="page-title">Owner List</div>
          <div className="page-sub">Your own book of business owners — name, company, type. Upload a spreadsheet or add them one by one.</div>
        </div>
        <div className="ub-head-acts">
          <button className="btn" onClick={() => setAdding((a) => !a)} data-testid="ow-add-toggle"><Plus size={13} />Add owner</button>
          <input ref={fileRef} type="file" accept=".csv,.tsv,text/csv" style={{ display: "none" }} onChange={onFile} data-testid="ow-file" />
          <button className="btn btn-primary" onClick={() => fileRef.current?.click()} data-testid="ow-upload"><Upload size={13} />Upload list</button>
          <button className="btn" onClick={exportCsv} disabled={!rows.length} data-testid="ow-export"><Download size={13} />CSV</button>
        </div>
      </div>

      <Card title={`Owners — ${rows.length}`} className="table-scroll" testid="ow-card"
        right={<div className="om-toolbar">
          <input className="inp inp-sm" style={{ width: 200 }} placeholder="Search name, company, city…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ow-q" />
          <select className="inp inp-sm" style={{ width: 170 }} value={type} onChange={(e) => setType(e.target.value)} data-testid="ow-type-filter">
            <option value="">All lists</option>
            {typeOpts.map((t) => <option key={t.type} value={t.type}>{t.type} ({t.count})</option>)}
          </select>
          <button className={`btn btn-sm ${picked.length ? "btn-primary" : ""}`} onClick={addToCampaign} disabled={!picked.length} data-testid="ow-campaign"><Share2 size={12} />Campaign ({picked.length})</button>
          <button className="btn btn-sm" onClick={() => (withEmail.length ? setFolkOpen(true) : toast.error("Selected owners need an email address"))} disabled={!picked.length} data-testid="ow-folk"><Users size={12} />Folk</button>
          <button className="btn btn-sm" onClick={openCompose} disabled={!picked.length} title="Email selected owners" data-testid="ow-email"><Mail size={12} /></button>
        </div>}>
        {adding && (
          <div className="ow-add" data-testid="ow-add-form">
            {[["name", "Owner name"], ["company", "Company"], ["title", "Title"], ["email", "Email"], ["phone", "Phone"], ["city", "City"], ["state", "State"], ["revenue", "Revenue ($)"], ["employees", "Employees"], ["notes", "Notes"]].map(([k, label]) => (
              <div className="field" key={k}><label>{label}</label>
                <input className="inp" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`ow-f-${k}`} /></div>
            ))}
            <div className="field"><label>Type</label>
              <select className="inp" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="ow-f-type">
                <option value="">—</option>{TYPES.map((t) => <option key={t}>{t}</option>)}
              </select></div>
            <div className="field" style={{ alignSelf: "end" }}>
              <button className="btn btn-primary" onClick={add} data-testid="ow-save"><Save size={12} />Save owner</button>
            </div>
          </div>
        )}
        <div className="bf-counts">{Object.entries(counts).map(([k, v]) => <span key={k} className="chip">{k}: {v}</span>)}</div>
        <table className="tbl" data-testid="ow-table">
          <thead><tr>
            <th style={{ width: 28 }}><input type="checkbox" checked={rows.length > 0 && rows.every((r) => sel[r.id])} onChange={toggleAll} data-testid="ow-all" /></th>
            <th>Owner</th><th>Company</th><th style={{ minWidth: 150 }}>List</th><th>Contact</th><th>Location</th><th style={{ minWidth: 112 }}>Stage</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="om-empty" data-testid="ow-empty"><b>No owners yet.</b> Upload a CSV (Owner, Company, Type, Email, Phone, City, State) or add one manually.</td></tr>
            ) : rows.map((o) => (
              <tr key={o.id} data-testid={`ow-row-${o.id}`}>
                <td><input type="checkbox" checked={!!sel[o.id]} onChange={() => setSel((s) => ({ ...s, [o.id]: !s[o.id] }))} data-testid={`ow-check-${o.id}`} /></td>
                <td><b>{o.name || "—"}</b>{o.title ? <div className="om-sub">{o.title}</div> : null}</td>
                <td>{o.company}{o.website ? <div className="om-sub">{o.website.replace(/^https?:\/\/(www\.)?/, "")}</div> : null}</td>
                <td>
                  <select className="inp inp-sm" style={{ minWidth: 176 }} value={o.type || ""} onChange={(e) => patch(o, { type: e.target.value })} data-testid={`ow-type-${o.id}`}>
                    <option value="">—</option>
                    {[...new Set([...typeOpts.map((t) => t.type), ...TYPES, o.type].filter(Boolean))].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </td>
                <td style={{ fontSize: 11 }}>{o.email || <span className="om-sub">—</span>}{o.phone ? <div className="om-sub">{o.phone}</div> : null}</td>
                <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{[o.city, o.state].filter(Boolean).join(", ")}</td>
                <td>
                  <select className="inp inp-sm" style={{ minWidth: 104 }} value={o.stage || "Prospect"} onChange={(e) => patch(o, { stage: e.target.value })} data-testid={`ow-stage-${o.id}`}>
                    {STAGES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td><button className="btn btn-sm" onClick={() => del(o)} data-testid={`ow-del-${o.id}`}><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <AddToCampaignModal open={outreach.open} onClose={() => outreach.setOpen(false)} contacts={outreach.contacts}
        defaultGroupName={outreach.defaultName} source={outreach.source} dealName="" />
      <SendToFolkModal open={folkOpen} onClose={() => setFolkOpen(false)} source="owners" label={type || "Owner List"}
        ids={withEmail.map((o) => o.id)} dealName="" />
      <ComposeEmailModal open={compose.open} onClose={() => compose.setOpen(false)} config={compose.config} />
    </div>
  );
}
